<template>
  <header class="h-14 bg-white border-b border-gray-100/60 flex items-center justify-between gap-2 px-3 md:px-8 shadow-sm relative z-10 box-border overflow-hidden">
    <!-- 左侧：面包屑导航 -->
    <div class="flex items-center flex-shrink min-w-0 max-w-[52vw] md:flex-shrink-0 md:min-w-[200px] md:max-w-none">
      <el-breadcrumb separator="/">
        <el-breadcrumb-item 
          v-for="(item, index) in breadcrumb" 
          :key="index"
          :to="index !== breadcrumb.length - 1 ? getPath(item) : undefined"
        >
          <span 
            class="transition-colors inline-block max-w-[120px] truncate align-bottom md:max-w-none"
            :class="[
              index === breadcrumb.length - 1 ? 'text-gray-900 font-semibold cursor-default' : 'text-gray-400',
              index !== breadcrumb.length - 1 && getPath(item) ? 'hover:text-[#6366f1] cursor-pointer' : ''
            ]"
          >
            {{ item }}
          </span>
        </el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <!-- 中间：全局搜索 -->
    <div class="hidden md:flex flex-1 min-w-0 px-4 xl:px-8 justify-center">
      <div class="relative w-[280px] xl:w-[360px] focus-within:!w-[520px] transition-all duration-300 ease-out group">
        <el-input
          v-model="searchQuery"
          placeholder="搜索页面..."
          class="w-full h-11 transition-all rounded-xl border-none shadow-none"
          :class="{ 'ring-2 ring-indigo-500/30': isSearchFocused }"
          @focus="handleSearchFocus"
          @blur="handleSearchBlur"
          @keyup.enter="handleSearchEnter"
          style="--el-input-bg-color: #f3f4f6; --el-input-border-radius: 12px; --el-input-border-color: transparent; --el-input-hover-border-color: transparent; --el-input-focus-border-color: transparent;"
        >
          <template #prefix>
            <el-icon class="text-gray-400 text-lg ml-1"><Search /></el-icon>
          </template>
        </el-input>

        <!-- 搜索结果下拉面板 -->
        <transition 
          enter-active-class="transition ease-out duration-100" 
          enter-from-class="transform opacity-0 scale-95" 
          enter-to-class="transform opacity-100 scale-100" 
          leave-active-class="transition ease-in duration-75" 
          leave-from-class="transform opacity-100 scale-100" 
          leave-to-class="transform opacity-0 scale-95"
        >
          <div v-show="showSearchResults" class="absolute top-14 left-0 w-full bg-white/95 backdrop-blur-md border border-gray-100 shadow-xl rounded-xl overflow-hidden z-50">
            <div class="max-h-[60vh] overflow-y-auto overscroll-contain py-2">
              <template v-if="filteredGroups.length > 0">
                <div v-for="(group, idx) in filteredGroups" :key="group.name" class="mb-1">
                  <div class="px-4 py-1.5 flex items-center">
                    <span class="text-xs font-bold tracking-wide text-gray-400">{{ group.name }}</span>
                    <div class="ml-3 flex-1 h-px bg-gray-100"></div>
                  </div>
                  <ul>
                    <li v-for="item in group.items" :key="item.path" @mousedown.prevent="handleResultClick(item)" class="px-4 py-2 hover:bg-indigo-50/60 cursor-pointer flex items-center group/item transition-colors">
                      <div class="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center mr-3 group-hover/item:bg-indigo-100/50 transition-colors shrink-0">
                        <el-icon class="text-gray-400 group-hover/item:text-indigo-500 transition-colors text-lg"><Document /></el-icon>
                      </div>
                      <div class="flex-1 overflow-hidden">
                        <div class="text-sm font-medium text-gray-800 break-words" v-html="highlight(item.title)"></div>
                        <div class="text-xs text-gray-400 mt-0.5 flex flex-wrap items-center gap-1 leading-tight" v-html="highlight(item.breadcrumbPath)"></div>
                      </div>
                    </li>
                  </ul>
                </div>
              </template>
              <div v-else class="px-4 py-12 text-center text-gray-400 flex flex-col items-center">
                <el-icon class="text-4xl mb-2 text-gray-200"><Search /></el-icon>
                <p class="text-sm">未找到与 "{{ searchQuery }}" 相关的页面</p>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- 右侧：操作区与个人信息 -->
    <div class="flex items-center space-x-2 md:space-x-4 flex-shrink-0 min-w-0">

      <!-- ★ 全局行业切换器 ★ -->
      <el-dropdown trigger="click" @command="handleIndustrySwitch">
        <div
          class="rounded-full px-3.5 py-1.5 bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-all duration-200 cursor-pointer flex items-center gap-2 text-sm text-gray-700 shadow-sm select-none"
        >
          <span class="text-base leading-none">{{ currentMeta.emoji }}</span>
          <span class="hidden sm:inline font-medium whitespace-nowrap">{{ currentMeta.label }}</span>
          <el-icon class="text-xs text-gray-400 ml-0.5"><ArrowDown /></el-icon>
        </div>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item
              v-for="(meta, key) in industryMeta"
              :key="key"
              :command="key"
              :class="{ 'is-active': tenantConfig.currentIndustry === key }"
            >
              <div class="flex items-center gap-3 py-1">
                <span class="text-lg">{{ meta.emoji }}</span>
                <div class="flex flex-col">
                  <span class="font-medium text-gray-800 text-sm">{{ meta.label }}</span>
                  <span class="text-xs text-gray-400">{{ meta.description }}</span>
                </div>
                <el-icon v-if="tenantConfig.currentIndustry === key" class="ml-auto text-green-500"><CircleCheckFilled /></el-icon>
              </div>
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>

      <!-- 功能图标组 -->
      <div class="hidden sm:flex items-center space-x-2">
        <!-- 通知 -->
        <NoticeBell />

        <!-- 全屏 -->
        <button 
          @click="toggleFullscreen"
          class="p-2.5 rounded-full hover:bg-gray-50 transition-colors group"
          title="全屏"
        >
          <el-icon class="text-xl text-gray-500 group-hover:text-gray-900 transition-colors">
            <FullScreen v-if="!isFullscreen" />
            <CopyDocument v-else />
          </el-icon>
        </button>

      </div>

      <!-- 分隔线 -->
      <div class="hidden sm:block w-px h-8 bg-gray-200"></div>

      <!-- 个人信息 -->
      <div class="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded-xl transition-colors">
        <div class="hidden md:flex flex-col items-end mr-3">
          <span class="text-[13px] font-bold text-gray-900 leading-tight">{{ displayName }}</span>
          <div class="flex items-center gap-1">
            <span v-if="isSuperUser" class="text-[10px] text-amber-500 font-bold">👑</span>
            <span class="text-[11px] font-medium" :class="isSuperUser ? 'text-amber-500' : 'text-gray-400'">{{ displayRole }}</span>
          </div>
        </div>
        <!-- 渐变头像占位 -->
        <div class="w-9 h-9 md:w-10 md:h-10 rounded-full border-2 border-white shadow-sm flex items-center justify-center overflow-hidden"
          :class="isSuperUser ? 'bg-gradient-to-br from-amber-500 to-orange-600' : 'bg-gradient-to-br from-[#6366f1] to-[#8b5cf6]'"
        >
          <span class="text-white font-bold text-sm">{{ displayAvatar }}</span>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { Search, FullScreen, CopyDocument, ArrowDown, CircleCheckFilled, Document } from '@element-plus/icons-vue'
import NoticeBell from './NoticeBell.vue'
import { toast } from '../utils/toast'
import { tenantConfig, industryMeta, type IndustryType } from '../config/tenant'
import { authState, getPrimaryRoleName, isSuperAdmin } from '../config/auth'

interface Props {
  breadcrumb?: string[]
}

const props = withDefaults(defineProps<Props>(), {
  breadcrumb: () => []
})

const router = useRouter()

const getPath = (item: string) => {
  if (item === '首页') return '/dashboard'
  
  const targetRoute = router.getRoutes().find(r => 
    r.meta?.title === item && !r.path.includes(':')
  )
  
  return targetRoute ? targetRoute.path : undefined
}

const searchQuery = ref('')
const isSearchFocused = ref(false)
const isFullscreen = ref(false)

// --------------------------------------------------------
// 页面全局检索逻辑
// --------------------------------------------------------
interface SearchableRoute {
  path: string
  title: string
  breadcrumb: string[]
  breadcrumbPath: string
  groupName: string
}

const searchableRoutes = ref<SearchableRoute[]>([])

// 组件挂载时提取所有可检索的页面路由
onMounted(() => {
  const routes = router.getRoutes()
  const list: SearchableRoute[] = []
  
  routes.forEach(r => {
    // 排除无标题、带有动态参数详情页、以及移动端路由
    if (!r.meta?.title || r.path.includes(':') || r.meta?.mobile) return
    
    const breadcrumb = (r.meta.breadcrumb as string[]) || []
    // 使用面包屑第二级作为业务域分组名，兜底为“其他模块”
    const groupName = breadcrumb.length > 1 ? breadcrumb[1] : '其他模块'
    const breadcrumbPath = breadcrumb.join(' / ')
    
    list.push({
      path: r.path,
      title: r.meta.title as string,
      breadcrumb,
      breadcrumbPath,
      groupName
    })
  })
  
  searchableRoutes.value = list
})

// 控制搜索下拉面板是否显示
const showSearchResults = computed(() => {
  return isSearchFocused.value && searchQuery.value.trim().length > 0
})

// 根据关键词过滤并分组
const filteredGroups = computed(() => {
  const query = searchQuery.value.trim().toLowerCase()
  if (!query) return []
  
  const matched = searchableRoutes.value.filter(item => 
    item.title.toLowerCase().includes(query) || 
    item.breadcrumbPath.toLowerCase().includes(query)
  )
  
  const groupsMap = new Map<string, SearchableRoute[]>()
  matched.forEach(item => {
    if (!groupsMap.has(item.groupName)) {
      groupsMap.set(item.groupName, [])
    }
    groupsMap.get(item.groupName)!.push(item)
  })
  
  return Array.from(groupsMap.entries()).map(([name, items]) => ({
    name,
    items
  }))
})

// 高亮命中关键字
const highlight = (text: string) => {
  const query = searchQuery.value.trim()
  if (!query) return text
  // 规避正则特殊字符
  const safeQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${safeQuery})`, 'gi')
  return text.replace(regex, `<span class="text-indigo-600 font-bold bg-indigo-50 px-0.5 rounded">\$1</span>`)
}

const handleSearchFocus = () => {
  isSearchFocused.value = true
}

const handleSearchBlur = () => {
  // 延迟关闭面板，保证下拉列表的点击事件(mousedown)能够被处理
  setTimeout(() => {
    isSearchFocused.value = false
  }, 150)
}

const handleSearchEnter = () => {
  if (filteredGroups.value.length > 0 && filteredGroups.value[0].items.length > 0) {
    handleResultClick(filteredGroups.value[0].items[0])
  } else if (searchQuery.value.trim()) {
    toast.warning('未找到相关页面')
  }
}

const handleResultClick = (item: SearchableRoute) => {
  searchQuery.value = ''
  isSearchFocused.value = false
  router.push(item.path)
}

// --------------------------------------------------------
// 当前行业元数据
const currentMeta = computed(() => industryMeta[tenantConfig.currentIndustry])

// Phase 11: 动态用户信息
const getSysUserDisplayName = () => {
  const sysUser = authState.sysUser as any
  return sysUser?.realName || sysUser?.real_name || sysUser?.username || localStorage.getItem('username') || ''
}

const displayName = computed(() => authState.currentUser?.name || getSysUserDisplayName() || '未登录')
const displayRole = computed(() => getPrimaryRoleName())
const displayAvatar = computed(() => displayName.value.slice(0, 1) || 'U')
const isSuperUser = computed(() => isSuperAdmin())

// 行业切换
const handleIndustrySwitch = (command: IndustryType) => {
  if (command === tenantConfig.currentIndustry) return
  tenantConfig.currentIndustry = command
  const meta = industryMeta[command]
  toast.success(`系统已全局切换至 ${meta.emoji} ${meta.label}`)
  // 延迟刷新，让 toast 先展示
  setTimeout(() => {
    window.location.reload()
  }, 800)
}

// 全屏切换
const toggleFullscreen = () => {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen().then(() => {
      isFullscreen.value = true
      toast.success('已进入全屏模式')
    }).catch(() => {
      toast.error('无法进入全屏模式')
    })
  } else {
    document.exitFullscreen().then(() => {
      isFullscreen.value = false
      toast.success('已退出全屏模式')
    })
  }
}

</script>

<style scoped>
/* 行业切换器下拉菜单中的当前选中项高亮 */
:deep(.el-dropdown-menu__item.is-active) {
  background-color: #f0fdf4;
}
</style>
